import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    Paper, 
    Typography, 
    Button, 
    Box, 
    Grid, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    TextField,
    Link,
    Chip,
    CircularProgress,
    Tooltip,
    MenuItem,
    FormControlLabel,
    Switch,
    Slider,
    ImageList,
    ImageListItem,
    ImageListItemBar,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Snackbar,
    Alert,
    Divider,
    FormControl,
    InputLabel,
    Select
} from '@mui/material';
import { format } from 'date-fns';
import { Link as RouterLink } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import UpdateIcon from '@mui/icons-material/Update';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CalendarDayIcon from '@mui/icons-material/CalendarToday';
import GroupIcon from '@mui/icons-material/Group';
import DescriptionIcon from '@mui/icons-material/Description';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import PaletteIcon from '@mui/icons-material/Palette';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import TrafficIcon from '@mui/icons-material/Traffic';
import DevicesIcon from '@mui/icons-material/Devices';
import CellWifiIcon from '@mui/icons-material/CellWifi';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import ScreenRotationIcon from '@mui/icons-material/ScreenRotation';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import LinkIcon from '@mui/icons-material/Link';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StorageIcon from '@mui/icons-material/Storage';
import AccessTimeIcon from '@mui/icons-material/AccessTime';


import API_BASE_URL from '../config';
import './Device.css';

const TimeDialog = ({ open, onClose, primaryColor, textColor }) => {
  const navigate = useNavigate();
  const { deviceId } = useParams();
  const [deviceTime, setDeviceTime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoTimeZone, setAutoTimeZone] = useState(true);
  const [autoSyncOnBoot, setAutoSyncOnBoot] = useState(false);
  const [timeZoneMode, setTimeZoneMode] = useState(0); // 0: Manual, 1: Auto, 2: GPS
  const [selectedTimeZone, setSelectedTimeZone] = useState('Australia/Brisbane');
  const [timeZoneMessage, setTimeZoneMessage] = useState('');
  const [timeMode, setTimeMode] = useState('device'); // 'device', 'auto', or 'gps'
  const [packageTimeSync, setPackageTimeSync] = useState(false);

  const timeZones = [
    { value: 'Australia/Brisbane', label: '(GMT+10:00) Brisbane' },
    { value: 'Australia/Sydney', label: '(GMT+10:00) Sydney, Melbourne' },
    { value: 'Australia/Adelaide', label: '(GMT+09:30) Adelaide' },
    { value: 'Australia/Darwin', label: '(GMT+09:30) Darwin' },
    { value: 'Australia/Perth', label: '(GMT+08:00) Perth' },
    { value: 'Australia/Eucla', label: '(GMT+08:45) Eucla' },
    { value: 'Australia/Lord_Howe', label: '(GMT+10:30) Lord Howe Island' },
    { value: 'Antarctica/Macquarie', label: '(GMT+11:00) Macquarie Island' },
    { value: 'Pacific/Auckland', label: '(GMT+12:00) Auckland, Wellington' },
    { value: 'Pacific/Chatham', label: '(GMT+12:45) Chatham Islands' },
    { value: 'Pacific/Midway', label: '(GMT-11:00) Midway Island, Samoa' },
    { value: 'America/Adak', label: '(GMT-10:00) Hawaii-Aleutian' },
    { value: 'Etc/GMT+10', label: '(GMT-10:00) Hawaii' },
    { value: 'Pacific/Marquesas', label: '(GMT-09:30) Marquesas Islands' },
    { value: 'Pacific/Gambier', label: '(GMT-09:00) Gambier Islands' },
    { value: 'America/Anchorage', label: '(GMT-09:00) Alaska' },
    { value: 'America/Los_Angeles', label: '(GMT-08:00) Pacific Time (US & Canada)' },
    { value: 'America/Denver', label: '(GMT-07:00) Mountain Time (US & Canada)' },
    { value: 'America/Chicago', label: '(GMT-06:00) Central Time (US & Canada)' },
    { value: 'America/New_York', label: '(GMT-05:00) Eastern Time (US & Canada)' },
    { value: 'America/Caracas', label: '(GMT-04:30) Caracas' },
    { value: 'America/Halifax', label: '(GMT-04:00) Atlantic Time (Canada)' },
    { value: 'America/St_Johns', label: '(GMT-03:30) Newfoundland' },
    { value: 'America/Sao_Paulo', label: '(GMT-03:00) Brasilia' },
    { value: 'America/Argentina/Buenos_Aires', label: '(GMT-03:00) Buenos Aires' },
    { value: 'America/Godthab', label: '(GMT-03:00) Greenland' },
    { value: 'Atlantic/Azores', label: '(GMT-01:00) Azores' },
    { value: 'Europe/London', label: '(GMT+00:00) London, Dublin' },
    { value: 'Europe/Paris', label: '(GMT+01:00) Paris, Amsterdam, Berlin' },
    { value: 'Europe/Athens', label: '(GMT+02:00) Athens, Istanbul' },
    { value: 'Asia/Dubai', label: '(GMT+04:00) Dubai, Abu Dhabi' },
    { value: 'Asia/Kabul', label: '(GMT+04:30) Kabul' },
    { value: 'Asia/Karachi', label: '(GMT+05:00) Karachi' },
    { value: 'Asia/Kolkata', label: '(GMT+05:30) Kolkata, Mumbai' },
    { value: 'Asia/Kathmandu', label: '(GMT+05:45) Kathmandu' },
    { value: 'Asia/Dhaka', label: '(GMT+06:00) Dhaka' },
    { value: 'Asia/Yangon', label: '(GMT+06:30) Yangon' },
    { value: 'Asia/Bangkok', label: '(GMT+07:00) Bangkok, Hanoi' },
    { value: 'Asia/Shanghai', label: '(GMT+08:00) Beijing, Shanghai' },
    { value: 'Asia/Tokyo', label: '(GMT+09:00) Tokyo, Seoul' },
    { value: 'Pacific/Noumea', label: '(GMT+11:00) Noumea' },
    { value: 'Pacific/Kiritimati', label: '(GMT+14:00) Kiritimati' },
  ];

  const [deviceSchedules, setDeviceSchedules] = useState([]);
  const [deviceUpdateLogs, setDeviceUpdateLogs] = useState([]);

  const [calendarSchedule, setCalendarSchedule] = useState(null);
  const [packageAllowance, setPackageAllowance] = useState(false);

  
  

  useEffect(() => {
    if (open) {
      fetchPackageAllowance();
      fetchTimeSettings();
      fetchDeviceTime();
    }
  }, [open, deviceId]);

  const fetchPackageAllowance = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/package/time_sync`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      setPackageTimeSync(data === 1);

    } catch (error) {
      console.error('Error fetching user package:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/device/time-settings/${deviceId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const settings = await response.json();

      setAutoTimeZone(settings.auto_time_zone === 1);
      setAutoSyncOnBoot(settings.auto_sync_on_boot === 1);
      setSelectedTimeZone(settings.timezone);
    } catch (error) {
      console.error('Error fetching time settings:', error);
      setTimeZoneMessage('Failed to fetch time settings');
    }
  };

  const handleAutoSyncOnBootChange = (event) => {
    setAutoSyncOnBoot(event.target.checked);
  };

  const handleTimeModeChange = (event) => {
    setTimeMode(event.target.value);
  };

  const formatTime = (date) => {
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const handleTimeZoneModeChange = (event) => {
    setTimeZoneMode(Number(event.target.value));
  };

  const fetchDeviceTime = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/device/device-time/${deviceId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setDeviceTime(data);
    } catch (error) {
      console.error('Error fetching device time:', error);
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    if (open) {
      fetchDeviceTime();
    }
  }, [open, fetchDeviceTime]);

  const formatDeviceTime = (time) => {
    if (!time) return 'N/A';
    return `${time.dayOfWeek}, ${time.month}/${time.day}/${time.year}, ${time.hour}:${time.minute}:${time.second}`;
  };

  const getCurrentTime = useCallback(() => {
    return new Date().toLocaleString('en-US', {
      hour12: false,
      weekday: 'long',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: autoTimeZone ? undefined : selectedTimeZone,
    });
  }, [autoTimeZone, selectedTimeZone]);


  const syncLocalTime = async () => {
    try {
      const now = new Date();
      const localTime = {
        second: now.getSeconds(),
        minute: now.getMinutes(),
        hour: now.getHours(),
        day: now.getDate(),
        month: now.getMonth() + 1,
        year:(now.getFullYear() % 100),
        dayOfWeek: now.getDay(),
      };
      
      const response = await fetch(`${API_BASE_URL}/device/sync-time/${deviceId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(localTime),
      });
      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      setTimeZoneMessage(result.message);
      await fetchDeviceTime();
    } catch (error) {
      console.error('Error syncing local time:', error);
      setTimeZoneMessage('Failed to sync local time');
    }
  };

  const getUserTimeZone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  };

  useEffect(() => {
    if (autoTimeZone) {
      setSelectedTimeZone(getUserTimeZone());
    }
  }, [autoTimeZone]);

  const handleAutoTimeZoneChange = (event) => {
    const isAutoTimeZone = event.target.checked;
    setAutoTimeZone(isAutoTimeZone);
    if (isAutoTimeZone) {
      setSelectedTimeZone(getUserTimeZone());
    }
  };

  const handleChangeTimezone = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/device/update-timezone/${deviceId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          autoTimeZone,
          selectedTimeZone,
          autoSyncOnBoot
        })
      });
      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }
      if (response.ok) {
        setTimeZoneMessage('Update successful');
        fetchDeviceTime(); // Refresh device time after update
      } else {
        setTimeZoneMessage('Failed to update');
      }
    } catch (error) {
      console.error('Error updating timezone:', error);
      setTimeZoneMessage('Failed to update');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      PaperProps={{
        style: {
          backgroundColor: primaryColor,
          color: textColor,
        },
      }}
    >
      <DialogTitle sx={{ color: `${textColor} !important` }}>Device Time Details</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <Typography variant="subtitle1">
              Device Time: {deviceTime ? formatDeviceTime(deviceTime) : 'Loading...'}
            </Typography>
            <Typography variant="subtitle1">
              {`Local  Time`}: {formatTime(new Date())}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel id="time-zone-mode-label" sx={{ color: `${textColor} !important` }}>Time Zone Mode</InputLabel>
              <Select
                labelId="time-zone-mode-label"
                value={timeZoneMode}
                onChange={handleTimeZoneModeChange}
                sx={{
                  color: `${textColor} !important`,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: `${textColor} !important` },
                  '& .MuiSvgIcon-root': { color: `${textColor} !important` },
                }}
              >
                <MenuItem value={0}>Manual</MenuItem>
                <MenuItem value={1}>Auto</MenuItem>
                <MenuItem value={2} disabled>GPS</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel id="time-zone-label" sx={{ color: `${textColor} !important` }}>Time Zone</InputLabel>
              <Select
                labelId="time-zone-label"
                value={selectedTimeZone}
                onChange={(e) => setSelectedTimeZone(e.target.value)}
                disabled={timeZoneMode !== 0}
                sx={{
                  color: `${textColor} !important`,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: `${textColor} !important` },
                  '& .MuiSvgIcon-root': { color: `${textColor} !important` },
                }}
              >
                {timeZones.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={autoSyncOnBoot}
                  onChange={(e) => setAutoSyncOnBoot(e.target.checked)}
                  color="primary"
                />
              }
              label="Auto Sync on Boot"
              sx={{ color: `${textColor} !important` }}
            />
          </Grid>
          <Grid item xs={6}>
            <Button 
              onClick={syncLocalTime} 
              sx={{ 
                color: `${textColor} !important`,
                borderColor: `${textColor} !important`,
                width: '100%'
              }}
              variant="outlined"
            >
              Sync Local Time
            </Button>
          </Grid>
        </Grid>
        {timeZoneMessage && (
          <Typography 
            variant="body2" 
            sx={{ 
              color: timeZoneMessage.includes('successful') ? 'green' : 'red',
              mt: 2 
            }}
          >
            {timeZoneMessage}
          </Typography>
        )}
        {!packageTimeSync && (
          <Typography 
            variant="body2" 
            sx={{ 
              color: timeZoneMessage.includes('successful') ? 'green' : 'red',
              mt: 2 
            }}
          >
            Time Sync not available in your current package.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleChangeTimezone} sx={{ color: packageTimeSync ? `#4CAF50 !important` : `grey` }} disabled={!packageTimeSync}>Apply</Button>
        <Button onClick={onClose} sx={{ color: `${textColor} !important` }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};


function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const Device = () => {
    const { deviceId } = useParams();
    const navigate = useNavigate();
    const [device, setDevice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isLocked, setIsLocked] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogError, setDialogError] = useState('');
    const [password, setPassword] = useState('');

    const [openTimeDialog, setOpenTimeDialog] = useState(false);
    const [deviceTime, setDeviceTime] = useState(null);
    const [deviceTimeLoading, setDeviceTimeLoading] = useState(false);
    const [storageRemainingLoading, setStorageRemainingLoading] = useState(false);

    const [primaryColor, setPrimaryColor] = useState(localStorage.getItem('primaryColor') || '#007bff');
    const [secondaryColor, setSecondaryColor] = useState(localStorage.getItem('secondaryColor') || '#6c757d');
    const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
    const [textColor, setTextColor] = useState(darkMode ? '#ffffff' : '#000000');

    const [cabinTemp, setCabinTemp] = useState(null);
    const [batteryTemp, setBatteryTemp] = useState(null);
    const [cabinTempLoading, setCabinTempLoading] = useState(true);
    const [cabinTempError, setCabinTempError] = useState(null);

    const [displayLive, setDisplayLive] = useState(false);
    const [playing, setPlaying] = useState(null);
    const [mediaImages, setMediaImages] = useState({});
    const [hasDisplayChanges, setHasDisplayChanges] = useState(false);

    const [description, setDescription] = useState('');

    const [storageRemaining, setStorageRemaining] = useState(0);

    const [brightness, setBrightness] = useState(Array(24).fill(0));
    const [originalBrightness, setOriginalBrightness] = useState(Array(24).fill(0));
    const [hasBrightnessChanges, setHasBrightnessChanges] = useState(false);
    const [brightnessLive, setBrightnessLive] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [lastDraggedHour, setLastDraggedHour] = useState(null);

    const [userMediaImages, setUserMediaImages] = useState({});
    const [openImageDialog, setOpenImageDialog] = useState(false);
    const [imageDialogType, setImageDialogType] = useState('');
    const [selectedImage, setSelectedImage] = useState('');
    const [isLoadingImages, setIsLoadingImages] = useState(false);
    
    const [deviceSchedules, setDeviceSchedules] = useState([]);
    const [deviceUpdateLogs, setDeviceUpdateLogs] = useState([]);

    const [calendarSchedule, setCalendarSchedule] = useState(null);
    const [packageAllowance, setPackageAllowance] = useState(false);

    const [serviceLogDialogOpen, setServiceLogDialogOpen] = useState(false);
    const [selectedServiceLog, setSelectedServiceLog] = useState(null);
    const [serviceImages, setServiceImages] = useState([]);

    const [enlargedImage, setEnlargedImage] = useState(null);
    const [imageDialogOpen, setImageDialogOpen] = useState(false);

    const [imageScale, setImageScale] = useState(1);
    const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
    // const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [imageRotation, setImageRotation] = useState(0);
    const [enlargedImageIndex, setEnlargedImageIndex] = useState(-1);


    const [snackbar, setSnackbar] = useState({
      open: false,
      message: '',
      severity: 'success',
    });

    const indicatorStyle = {
        marginLeft: '10px',
        fontSize: '0.875rem',
        fontWeight: 'bold',
    };

    useEffect(() => {
        fetchDeviceDetails();
      }, [deviceId]);
    
    useEffect(() => {
        if (device) {
          fetchCabinTemp(deviceId);
          fetchPlaying(deviceId);
          fetchBrightness(deviceId);
          fetchStorageRemaining(deviceId);
          fetchDeviceTime(deviceId);
        }
      }, [device]);

    useEffect(() => {
        const checkLocalStorage = () => {
        const storedPrimaryColor = localStorage.getItem('primaryColor');
        const storedSecondaryColor = localStorage.getItem('secondaryColor');
        const storedDarkMode = localStorage.getItem('darkMode') === 'true';

        setPrimaryColor(storedPrimaryColor || (storedDarkMode ? '#0f0f0f' : '#f5f5f5'));
        setSecondaryColor(storedSecondaryColor || (storedDarkMode ? '#1a1a1a' : '#e0e0e0'));
        setDarkMode(storedDarkMode);
        setTextColor(storedDarkMode ? '#ffffff' : '#000000');
        };

        checkLocalStorage();
        const intervalId = setInterval(checkLocalStorage, 200);

        return () => clearInterval(intervalId);
    }, []);

    useEffect(() => {
      const fetchPackageAllowance = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/users/package/calendar_schedule`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          if (response.status === 401) {
            navigate('/unauthorized');
            return;
          }

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();

          setPackageAllowance(data === 1);

        } catch (error) {
          console.error('Error fetching user package:', error);
        }
      };

      const fetchCalendarSchedule = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/device/device-calendar-schedule/${deviceId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            setCalendarSchedule(data);
          }
        } catch (error) {
          console.error('Error fetching calendar schedule:', error);
        }
      };
      
      fetchPackageAllowance();
      fetchCalendarSchedule();
    }, []);

  const fetchDeviceDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/device/details/${deviceId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(data); // debug
      setDevice(data);
      setDescription(data.description || '');
      setError(null);
    } catch (error) {
      console.error('Error fetching device details:', error);
      setError('Failed to fetch device details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCabinTemp = async (deviceId) => {
    try {
      setCabinTempLoading(true);
      const response = await fetch(`${API_BASE_URL}/device/device-cabin-temp/${deviceId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();

      setCabinTemp(data.cabinTemperature);
      setCabinTempError(null);
      setBatteryTemp(data.batteryTemprature);
    } catch (error) {
      console.error('Error fetching cabin temperature:', error);
      setCabinTempError('Failed to retrieve cabin temperature');
    } finally {
      setCabinTempLoading(false);
    }
  };

  const fetchStorageRemaining = async (deviceId) => {
    setStorageRemainingLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/device/device-storage-remaining/${deviceId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setStorageRemaining(data.storageRemaining);
    } catch (error) {
      console.error('Error fetching device storage:', error);
      setCabinTempError('Failed to retrieve device storage');
    } finally {
      setStorageRemainingLoading(false);
    }
  };

  const formatDeviceTime = (time) => {
    if (!time) return 'N/A';
    return `${time.dayOfWeek}, ${time.month}/${time.day}/${time.year}, ${time.hour}:${time.minute}:${time.second}`;
  };

  const fetchDeviceTime = async (deviceId) => {
    try {
      setDeviceTimeLoading(true);
      const response = await fetch(`${API_BASE_URL}/device/device-time/${deviceId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setDeviceTime(data);
    } catch (error) {
      console.error('Error fetching device storage:', error);
    } finally {
      setDeviceTimeLoading(false);
    }
  };

  const fetchDeviceTimeRefresh = useCallback(async () => {
    try {
      setDeviceTimeLoading(true);
      const response = await fetch(`${API_BASE_URL}/device/device-time/${deviceId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setDeviceTime(data);
    } catch (error) {
      console.error('Error fetching device storage:', error);
    } finally {
      setDeviceTimeLoading(false);
    }
  });

  const fetchPlaying = async (deviceId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/device/playing/${deviceId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (data) {
        setPlaying(data);
        setDisplayLive(true);
      } else {
        // If no data, use update log as the backup to display, warning message.
        if (device.updateLogs && device.updateLogs.length > 0) {
            const newestUpdateLog = device.updateLogs.sort((a, b) => 
                new Date(b.created_at) - new Date(a.created_at)
            )[0];

            const historyPlaying = {
                "minSpeed": newestUpdateLog.min_speed || 0,
                "speedLimit": newestUpdateLog.speed_limit || 0,
                "displayOn": newestUpdateLog.stay_on_time || 0,
                "regularContent": newestUpdateLog.regular_display_mode || 0,
                "speedingContent": newestUpdateLog.speeding_display_mode || 0,
                "fontSize": 0,
                "flash": newestUpdateLog.flash || 0,
                "switchFreqOn": newestUpdateLog.switch_freq_on || 0,
                "switchFreqOff": newestUpdateLog.switch_freq_off || 0,
                "flashFreqOn": newestUpdateLog.flash_freq_on || 0,
                "flashFreqOff": newestUpdateLog.flash_freq_off || 0,
                "regularContentName":newestUpdateLog.regular_content || 'fail.gif',
                "speedingContentName": newestUpdateLog.speeding_content || 'fail.gif'
            }
            setPlaying(historyPlaying);
        } else {
            console.error('No data or update logs found');
        }
        setDisplayLive(false);
      }
      
      return data;
    } catch (error) {
      if (error.message.includes('401')) {
        navigate('/unauthorized');
      } else {
        console.error(`Error fetching playing status for device ID ${deviceId}:`, error);
      }
      return null;
    }
  };



  const fetchMediaImage = useCallback(async (mediaName) => {
    try {
        const response = await fetch(`${API_BASE_URL}/device/media-image/${encodeURIComponent(mediaName)}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.status === 401) {
          navigate('/unauthorized');
          return;
        }
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const blob = await response.blob();
        return URL.createObjectURL(blob);
    } catch (error) {
        console.error(`Error fetching media image for ${mediaName}:`, error);
        return null;
    }
  }, []);

  const fetchUserMediaList = useCallback(async () => {
    setIsLoadingImages(true);
    try {
      const response = await fetch(`${API_BASE_URL}/device/media-list`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const mediaFiles = await response.json();
      const imagePromises = mediaFiles.map(async (file) => {
        const imageResponse = await fetch(`${API_BASE_URL}/device/media-image/${encodeURIComponent(file.file_name)}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (!imageResponse.ok) {
          console.error(`Failed to fetch image for ${file.file_name}`);
          return null;
        }
        const blob = await imageResponse.blob();
        return { name: file.file_name, url: URL.createObjectURL(blob) };
      });

      const images = await Promise.all(imagePromises);
      const imageMap = images.reduce((acc, img) => {
        if (img) {
          acc[img.name] = img.url;
        }
        return acc;
      }, {});
      
      setUserMediaImages(imageMap);
    } catch (error) {
      console.error('Error fetching user media images:', error);
    } finally {
      setIsLoadingImages(false);
    }
  }, []);

  const fetchBrightness = async (deviceId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/device/brightness/${deviceId}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.status === 401) {
          navigate('/unauthorized');
          return;
        }
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (data?.brightness) {
          setBrightness(data.brightness);
          setBrightnessLive(true);
        } else {
          const newestUpdateLog = device.updateLogs.sort((a, b) => 
            new Date(b.created_at) - new Date(a.created_at)
          )[0];
          setBrightness(newestUpdateLog?.brightness || Array(24).fill(0));
          setBrightnessLive(false);
        }
    } catch (error) {
      if (error.message.includes('401')) {
        navigate('/unauthorized');
      } else {
        console.error('Error fetching cabin temperature:', error);
      }
    }
  };

  useEffect(() => {
    const fetchImages = async () => {
      const newMediaImages = {};
      if (playing) {
        const regularContentName = playing.regularContentName;
        const speedingContentName = playing.speedingContentName;
        if (regularContentName) {
            newMediaImages[regularContentName] = await fetchMediaImage(regularContentName);
        }
        if (speedingContentName) {
            newMediaImages[speedingContentName] = await fetchMediaImage(speedingContentName);
        }
      }
      setMediaImages(newMediaImages);
    };

    if (device && playing) {
      fetchImages();
    }
  }, [device, playing, fetchMediaImage]);
    
  const fetchDeviceSchedules = async (deviceId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/device/schedules/${deviceId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          console.log('No schedules found for this device');
          return [];
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching device schedules:', error);
      throw error;
    }
  };

  const fetchDeviceUpdateLogs = async (deviceId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/device/update-logs/${deviceId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          console.log('No update history found for this device');
          return [];
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching device update histories:', error);
      throw error;
    }
  };

  useEffect(() => {
    const loadDeviceSchedules = async () => {
      if (device) {
        try {
          const schedules = await fetchDeviceSchedules(device.id);
          setDeviceSchedules(schedules);
        } catch (error) {
          console.error('Failed to load device schedules:', error);
        }
      }
    };

    const loadDeviceUpdateHistories = async () => {
      if (device) {
        try {
          const updateLogs = await fetchDeviceUpdateLogs(device.id);
          setDeviceUpdateLogs(updateLogs);
        } catch (error) {
          console.error('Failed to load device update logs:', error);
        }
      }
    };

    loadDeviceSchedules();
    loadDeviceUpdateHistories();
  }, [device]);

  const formatScheduleTime = (schedule) => {
    const fullYear = 2000 + parseInt(schedule.year);
    const date = new Date(fullYear, schedule.month - 1, schedule.day, schedule.hour, schedule.minute);
    return date.toLocaleString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const handleOpenTimeDialog = (time) => {
    setOpenTimeDialog(true);
  };
  
  const handleCloseTimeDialog = () => {
    setOpenTimeDialog(false);
  };

  const handleUnlockClick = () => {
    if (isLocked) {
      setOpenDialog(true);
    } else {
      setIsLocked(true);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setPassword('');
  };

  const handleDescriptionUpdate = async (newDescription) => {
    try {
      const response = await fetch(`${API_BASE_URL}/device/update-description/${deviceId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ description: newDescription })
      });

      if (response.ok) {
        setDescription(newDescription);
        setSnackbar({
          open: true,
          message: 'Description updated successfully',
          severity: 'success'
        });
      } else {
        throw new Error('Failed to update description');
      }
    } catch (error) {
      console.error('Error updating description:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update description',
        severity: 'error'
      });
    }
  };

  const handlePlayingChange = useCallback((property, value) => {
    setPlaying(prev => ({
      ...prev,
      [property]: value
    }));
    setHasDisplayChanges(true);
  }, []);

  const handleDisplayUpdate = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/device/display-update/${deviceId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(playing)
      });

      if (response.status === 200) {
        setSnackbar({
          open: true,
          message: 'Display update successful',
          severity: 'success'
        });
        setHasDisplayChanges(false);
        const newlogs = await fetchDeviceUpdateLogs(deviceId);
        setDeviceUpdateLogs(newlogs);
      } else if (response.status === 201) {
        setSnackbar({
          open: true,
          message: 'Device cannot be connected at this moment. Update has been recorded and will be processed when device reconnects.',
          severity: 'warning'
        });
        setHasDisplayChanges(false);
        const newlogs = await fetchDeviceUpdateLogs(deviceId);
        setDeviceUpdateLogs(newlogs);
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      console.error('Error updating display:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update display',
        severity: 'error'
      });
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    if (!isLocked) {
      setHasDisplayChanges(false);
    }
  }, [isLocked]);
  
  const handleBrightnessUpdate = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/device/brightness-update/${deviceId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(brightness)
      });

      if (response.status === 200) {
        setSnackbar({
          open: true,
          message: 'Brightness update successful',
          severity: 'success'
        });
        setHasBrightnessChanges(false);
        const newlogs = await fetchDeviceUpdateLogs(deviceId);
        setDeviceUpdateLogs(newlogs);
      } else if (response.status === 201) {
        setSnackbar({
          open: true,
          message: 'Device cannot be connected at this moment. Update has been recorded and will be processed when device reconnects.',
          severity: 'warning'
        });
        setHasBrightnessChanges(false);
        const newlogs = await fetchDeviceUpdateLogs(deviceId);
        setDeviceUpdateLogs(newlogs);
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      console.error('Error updating brightness:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update brightness',
        severity: 'error'
      });
    }
  };
  

  useEffect(() => {
    if (device) {
      const initialBrightness = [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0];
      setBrightness(initialBrightness);
      setOriginalBrightness(initialBrightness);
    }
  }, [device]);

  const handleMouseDown = (hour) => {
    if (!isLocked) {
      setIsDragging(true);
      setLastDraggedHour(hour);
    }
  };

  const handleMouseEnter = (hour) => {
    if (isDragging && !isLocked) {
      const startHour = Math.min(lastDraggedHour, hour);
      const endHour = Math.max(lastDraggedHour, hour);
      const newBrightness = [...brightness];
      for (let i = startHour; i <= endHour; i++) {
        newBrightness[i] = newBrightness[lastDraggedHour];
      }
      setBrightness(newBrightness);
      setLastDraggedHour(hour);
      setHasBrightnessChanges(true);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setLastDraggedHour(null);
  };

  useEffect(() => {
    if (device) {
      const initialBrightness = [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0];
      setBrightness(initialBrightness);
      setOriginalBrightness(initialBrightness);
    }
  }, [device]);

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);



  const formatBuiltDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('default', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const handlePasswordSubmit = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/control-verification/${deviceId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ password })
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }

      if (response.ok) {
        setIsLocked(false);
        handleDialogClose();
      } else {
        const errorData = await response.json();
        setDialogError(errorData.message || 'Invalid password. Please try again.');
      }
    } catch (error) {
      console.error('Error during unlock:', error);
      setDialogError('An error occurred. Please try again.');
    }
  };

  const TopButton = ({ icon, onClick, label, isUnlockButton }) => (
    <Button
      onClick={onClick}
      startIcon={icon}
      sx={{
        color: textColor,
        backgroundColor: isUnlockButton 
          ? (isLocked ? 'red' : 'green')
          : secondaryColor,
        '&:hover': {
          backgroundColor: isUnlockButton
            ? (isLocked ? 'darkred' : 'darkgreen')
            : (darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'),
        },
        padding: '10px 20px',
        borderRadius: '5px',
      }}
    >
      {label}
    </Button>
  );

  const GridItem = ({ children, gridArea }) => (
    <Paper
      sx={{
        padding: '20px',
        backgroundColor: secondaryColor,
        color: textColor,
        borderRadius: '10px',
        gridArea: gridArea,
        height: '100%',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      }}
    >
      {children}
    </Paper>
  );

  const EditableDescription = ({ initialDescription, isLocked, onUpdate, textColor }) => {
    const [description, setDescription] = useState(initialDescription);
    const [hasChanges, setHasChanges] = useState(false);
  
    useEffect(() => {
      setDescription(initialDescription);
      setHasChanges(false);
    }, [initialDescription]);
  
    const handleDescriptionChange = (event) => {
      setDescription(event.target.value);
      setHasChanges(event.target.value !== initialDescription);
    };
  
    const handleUpdateDescription = () => {
      onUpdate(description);
      setHasChanges(false);
    };
  
    if (isLocked) {
      return (
        <Typography variant="body1" sx={{ color: textColor, whiteSpace: 'pre-wrap' }}>
          {description}
        </Typography>
      );
    }
  
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <TextField
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          value={description}
          onChange={handleDescriptionChange}
          sx={{
            textFieldSx
          }}
        />
        {hasChanges && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdateDescription}
            sx={{
              height: '56px',
              alignSelf: 'flex-start',
            }}
          >
            Update
          </Button>
        )}
      </Box>
    );
  };

  const EditableTextFieldLock = React.memo(({ label, initialValue, onChange, disabled, type = "text", min, max, step, select, children }) => {
    const [localValue, setLocalValue] = useState(initialValue);
    const debouncedValue = useDebounce(localValue, 500); // 500ms delay
  
    useEffect(() => {
      setLocalValue(initialValue);
    }, [initialValue]);
  
    useEffect(() => {
      if (debouncedValue !== initialValue) {
        onChange(debouncedValue);
      }
    }, [debouncedValue, initialValue, onChange]);
  
    const handleChange = (event) => {
      setLocalValue(event.target.value);
    };
  
    return (
      <TextField
        label={label}
        size='small'
        value={localValue}
        onChange={handleChange}
        disabled={disabled}
        type={type}
        InputProps={{
          inputProps: { min, max, step },
        }}
        select={select}
        sx={textFieldSxLock}
      >
        {children}
      </TextField>
    );
  });

  const EditableTextField = React.memo(({ label, initialValue, onChange, disabled, type = "text", min, max, step, select, children }) => {
    const [localValue, setLocalValue] = useState(initialValue);
    const debouncedValue = useDebounce(localValue, 500); // 500ms delay
  
    useEffect(() => {
      setLocalValue(initialValue);
    }, [initialValue]);
  
    useEffect(() => {
      if (debouncedValue !== initialValue) {
        onChange(debouncedValue);
      }
    }, [debouncedValue, initialValue, onChange]);
  
    const handleChange = (event) => {
      setLocalValue(event.target.value);
    };
  
    return (
      <TextField
        label={label}
        size='small'
        value={localValue}
        onChange={handleChange}
        disabled={disabled}
        type={type}
        InputProps={{
          inputProps: { min, max, step },
        }}
        select={select}
        sx={textFieldSx}
      >
        {children}
      </TextField>
    );
  });

  const BrightnessControl = ({ brightness, onChange, isLocked }) => {
    const [localBrightness, setLocalBrightness] = useState(() => {
      return Array.isArray(brightness) && brightness.length === 24
        ? brightness
        : Array(24).fill(0);
    });
  
    useEffect(() => {
      const newBrightness = Array.isArray(brightness) && brightness.length === 24
        ? brightness
        : Array(24).fill(0);
      setLocalBrightness(newBrightness);
    }, [brightness]);
  
    const handleBrightnessChange = useCallback((index) => (event, newValue) => {
      setLocalBrightness(prev => {
        const newBrightness = [...prev];
        newBrightness[index] = newValue;
        return newBrightness;
      });
    }, []);
  
    const handleChangeCommitted = useCallback(() => {
      if (!isLocked) {
        onChange(localBrightness);
      }
    }, [isLocked, onChange, localBrightness]);
  
    return (
      <Box sx={{ display: 'flex' }}>
        {[0, 1].map((column) => (
          <Box key={column} sx={{ flex: 1, pr: column === 0 ? 2 : 0, pl: column === 1 ? 2 : 0 }}>
            {localBrightness.slice(column * 12, (column + 1) * 12).map((value, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="caption" sx={{ minWidth: 35 }}>
                  {`${(column * 12 + index).toString().padStart(2, '0')}:00`}
                </Typography>
                <Slider
                  size="small"
                  value={value}
                  onChange={handleBrightnessChange(column * 12 + index)}
                  onChangeCommitted={handleChangeCommitted}
                  aria-labelledby={`brightness-slider-${column * 12 + index}`}
                  valueLabelDisplay="auto"
                  disabled={isLocked}
                  step={10}
                  marks
                  min={0}
                  max={100}
                  sx={{ 
                    mx: 1,
                    '& .MuiSlider-thumb': {
                      width: 12,
                      height: 12,
                      color: isLocked ? 'grey' : 'primary.main',
                    },
                    '& .MuiSlider-track': {
                      color: isLocked ? 'grey' : 'primary.main',
                    },
                  }}
                />
                <Typography variant="caption" sx={{ minWidth: 25 }}>
                  {value}%
                </Typography>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  const ContentPreview = ({ 
    contentMode, 
    regularContent, 
    speedingContent, 
    imageDisplayDuration, 
    speedDisplayDuration, 
    speedLimit,
    isRegular,
    flash,
    flashOnDuration,
    flashOffDuration
  }) => {
    const [currentDisplay, setCurrentDisplay] = useState('image');
    const [isFlashOn, setIsFlashOn] = useState(true);
    const [elapsedSwitchTime, setElapsedSwitchTime] = useState(0);
    const [elapsedFlashTime, setElapsedFlashTime] = useState(0);
  
    // Ensure minimum durations
    const safeImageDisplayDuration = 1000;
    const safeSpeedDisplayDuration = 1000;
    const safeFlashOnDuration = 500;
    const safeFlashOffDuration = 500;
  
    useEffect(() => {
      const interval = setInterval(() => {
        setElapsedSwitchTime(prev => prev + 100);
        setElapsedFlashTime(prev => prev + 100);
      }, 100);
  
      return () => clearInterval(interval);
    }, []);
  
    useEffect(() => {
      // Handle switching between image and speed
      if (contentMode === 3) { // Both mode
        if (currentDisplay === 'image' && elapsedSwitchTime >= safeImageDisplayDuration) {
          setCurrentDisplay('speed');
          setElapsedSwitchTime(0);
        } else if (currentDisplay === 'speed' && elapsedSwitchTime >= safeSpeedDisplayDuration) {
          setCurrentDisplay('image');
          setElapsedSwitchTime(0);
        }
      }
  
      // Handle flash effect
      if (flash) {
        if (isFlashOn && elapsedFlashTime >= safeFlashOnDuration) {
          setIsFlashOn(false);
          setElapsedFlashTime(0);
        } else if (!isFlashOn && elapsedFlashTime >= safeFlashOffDuration) {
          setIsFlashOn(true);
          setElapsedFlashTime(0);
        }
      }
    }, [contentMode, currentDisplay, elapsedSwitchTime, elapsedFlashTime, safeImageDisplayDuration, safeSpeedDisplayDuration, flash, isFlashOn, safeFlashOnDuration, safeFlashOffDuration]);
  
    const renderSpeedDisplay = () => (
      <Box 
        sx={{ 
          width: '100%', 
          height: '100%', 
          backgroundColor: 'black', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center'
        }}
      >
        <Typography 
          variant="h1" 
          sx={{ color: isRegular ? 'green' : 'red' }}
        >
          {speedLimit}
        </Typography>
      </Box>
    );
  
    const renderContent = () => {
      if (flash && !isFlashOn) {
        return <Box sx={{ width: '100%', height: '100%', backgroundColor: 'black' }} />;
      }
  
      if (contentMode === 3) { // Both mode
        return currentDisplay === 'image' 
          ? <img src={isRegular ? regularContent : speedingContent} alt="Content" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          : renderSpeedDisplay();
      } else if (contentMode === 2) { // Speed only
        return renderSpeedDisplay();
      } else { // Image only or default
        return <img src={isRegular ? regularContent : speedingContent} alt="Content" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />;
      }
    };
  
    return (
      <Box sx={{ width: '200px', height: '150px', border: '1px solid #ccc' }}>
        {renderContent()}
      </Box>
    );
  };

  const handleOpenImageDialog = (type) => {
    setImageDialogType(type);
    setOpenImageDialog(true);
    fetchUserMediaList();
  };

  const handleCloseImageDialog = () => {
    setOpenImageDialog(false);
    setSelectedImage('');
  };

  const handleImageSelect = (imageName) => {
    setSelectedImage(imageName);
    if (imageName) {
      if (imageDialogType === 'regular') {
        handlePlayingChange('regularContentName', imageName);
      } else if (imageDialogType === 'speeding') {
        handlePlayingChange('speedingContentName', imageName);
      }
    }
    handleCloseImageDialog();
  };

  const handleConfirmImageSelection = () => {
    if (selectedImage) {
      if (imageDialogType === 'regular') {
        handlePlayingChange('regularContentName', selectedImage);
      } else if (imageDialogType === 'speeding') {
        handlePlayingChange('speedingContentName', selectedImage);
      }
    }
    handleCloseImageDialog();
  };

  const handleCalendarScheduleToggle = async (enabled) => {
    try {
      const response = await fetch(`${API_BASE_URL}/device/device-calendar-schedule/${deviceId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ enabled: enabled ? 1 : 0 })
      });

      if (response.ok) {
        setCalendarSchedule(prev => ({
          ...prev,
          enabled: enabled ? 1 : 0
        }));
        setSnackbar({
          open: true,
          message: 'Calendar schedule status updated',
          severity: 'success'
        });
      } else {
        throw new Error('Failed to update calendar schedule status');
      }
    } catch (error) {
      console.error('Error updating calendar schedule status:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update calendar schedule status',
        severity: 'error'
      });
    }
  };

  const fetchServiceLogImages = async (serviceLogId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/device/service-log-images/${serviceLogId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status === 401) {
        navigate('/unauthorized');
        return;
      }

      if (response.ok) {
        const images = await response.json();
        setServiceImages(images || []);
      } else {
        throw new Error('Failed to fetch service log images');
      }
    } catch (error) {
      console.error('Error fetching service log images:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch service log images',
        severity: 'error'
      });
    }
  };

  // Add these functions after other handler functions
  const handleServiceLogClick = async (log) => {
    await fetchServiceLogImages(log.id);
    setSelectedServiceLog(log);
    setServiceLogDialogOpen(true);
  };

  const handleServiceLogDialogClose = () => {
    setServiceLogDialogOpen(false);
    setSelectedServiceLog(null);
  };

  if (loading) return <div className="loading" style={{ color: textColor, backgroundColor: primaryColor }}>Loading device details...</div>;
  if (error) return <div className="error" style={{ color: textColor, backgroundColor: primaryColor }}>{error}</div>;
  if (!device) return <div className="error" style={{ color: textColor, backgroundColor: primaryColor }}>No device found</div>;

  const textFieldSx = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      backgroundColor: (theme) => 
        !playing
          ? theme.palette.action.disabledBackground 
          : `${primaryColor} !important`,
    },
    '& .MuiInputBase-input': {
      color: `${textColor} !important`,
    },
    '& .MuiInputLabel-root': {
      color: `${textColor} !important`,
    },
    '& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.26) !important',
    },
    '& .Mui-disabled .MuiInputBase-input': {
      WebkitTextFillColor: `${textColor} !important`,
      color: `${textColor} !important`,
      backgroundColor: `${secondaryColor} !important`,
    },
  };

  const textFieldSxLock = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      backgroundColor: (theme) => 
        !playing || isLocked 
          ? theme.palette.action.disabledBackground 
          : `${primaryColor} !important`,
    },
    '& .MuiInputBase-input': {
      color: `${textColor} !important`,
    },
    '& .MuiInputLabel-root': {
      color: `${textColor} !important`,
    },
    '& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.26) !important',
    },
    '& .Mui-disabled .MuiInputBase-input': {
      WebkitTextFillColor: `${textColor} !important`,
      color: `${textColor} !important`,
      backgroundColor: `${secondaryColor} !important`,
    },
  };

  const fixedHeightGridItemStyle = {
    height: '500px',
    overflowY: 'auto',
  };

  return (
    <div className={`device ${darkMode ? 'dark-mode' : 'light-mode'}`} style={{ backgroundColor: primaryColor, padding: '20px' }}>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={snackbar.open}
        autoHideDuration={10000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0px' }}>
        <TopButton icon={<ArrowBackIcon />} onClick={() => navigate(-1)} label="Back" />
        <TopButton 
          icon={isLocked ? <LockIcon /> : <LockOpenIcon />} 
          onClick={handleUnlockClick} 
          label={isLocked ? "Unlock" : "Unlocked"} 
          isUnlockButton={true}
        />
        <TopButton icon={<RefreshIcon />} onClick={fetchDeviceDetails} label="Refresh" />
      </Box>
      <div className="device-grid">
        {/* Details 1 */}
        <GridItem gridArea="1 / 1 / 2 / 2">
          <Typography variant="h5" gutterBottom sx={{ color: textColor }}>
            {device.device_name || 'Device Name'}
          </Typography>
          
          <Grid container spacing={2}>
          <Grid item xs={12}>
              <EditableDescription
                initialDescription={description}
                isLocked={isLocked}
                onUpdate={handleDescriptionUpdate}
                textColor={textColor}
              />
            </Grid>
            {[
              { 
                icon: LocationOnIcon, 
                label: 'Location', 
                value: (
                  <Link href={`/#/maps`} sx={{ color: textColor, textDecoration: 'none' }}>
                    {`${device.street_address}, ${device.suburb}, ${device.state}, ${device.postcode}, ${device.direction}`}
                  </Link>
                )
              },
              { 
                icon: CalendarDayIcon, 
                label: 'Service Plan Expiry', 
                value: formatBuiltDate(device.service_plan || device.built_date)    
              },
              { 
                icon: GroupIcon, 
                label: 'Groups', 
                value: device.groups,
                isChipArray: true
              },
              { 
                icon: DescriptionIcon, 
                label: 'Preset', 
                value: device.preset,
                isChip: true
              },
              { 
                icon: CalendarMonthIcon,
                label: 'Calendar Schedule', 
                value: (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">
                      {calendarSchedule?.name || 'No schedule'}
                    </Typography>
                    <Switch
                      checked={calendarSchedule?.enabled === 1}
                      onChange={(e) => handleCalendarScheduleToggle(e.target.checked)}
                      disabled={!packageAllowance}
                      color="primary"
                    />
                  </Box>
                )
              },
            ].map((item, index) => (
              <Grid item xs={12} key={index}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center">
                    <item.icon sx={{ mr: 1, color: textColor }} />
                    <Typography variant="body2" sx={{ color: textColor }}>
                      {item.label}:
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    {item.isChipArray ? (
                      <Box display="flex" flexWrap="wrap" justifyContent="flex-end">
                        {Array.isArray(item.value) && item.value.length > 0 ? (
                          item.value.map((group) => (
                            <Chip
                              key={group.id}
                              label={group.group_name}
                              component={Link}
                              href={`/#/groups`}
                              clickable
                              size="small"
                              sx={{ 
                                ml: 0.5, 
                                mb: 0.5, 
                                color: textColor, 
                                borderColor: textColor,
                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                              }}
                              variant="outlined"
                            />
                          ))
                        ) : (
                          <Typography variant="body2" sx={{ color: textColor, fontStyle: 'italic' }}>
                            No group assigned
                          </Typography>
                        )}
                      </Box>
                    ) : item.isChip ? (
                    item.value && item.value.preset_name ? (
                      <Chip
                        label={item.value.preset_name}
                        component={Link}
                        href={`/#/presets`}
                        clickable
                        size="small"
                        sx={{ 
                          color: textColor, 
                          borderColor: textColor,
                          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                        }}
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" sx={{ color: textColor, fontStyle: 'italic' }}>
                        No preset assigned
                      </Typography>
                    )
                  ) : (
                      <Typography variant="body2" sx={{ color: textColor, fontWeight: 'bold' }}>
                        {item.value}
                      </Typography>
                    )}
                    {item.link && (
                      <Link href={item.link.url} sx={{ ml: 1, color: primaryColor }}>
                        {item.link.text}
                      </Link>
                    )}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </GridItem>
        {/* Details 2 */}
        <GridItem gridArea="1 / 2 / 2 / 3">
          <Typography variant="h6" gutterBottom sx={{ color: textColor }}>
            Technical Specifications
          </Typography>
          
          <Grid container spacing={2}>
            {[
              { icon: AspectRatioIcon, label: 'Screen Resolution', value: `${device.screen_width} x ${device.screen_height} px` },
              { icon: PaletteIcon, label: 'Color Order', value: device.color_order },
              { icon: WbSunnyIcon, label: 'Solar Panel', value: `${device.solar_size} W` },
              { icon: BatteryChargingFullIcon, label: 'Battery Capacity', value: `${device.battery_size} Ah` },
              { icon: DevicesIcon, label: 'Device Type', value: device.device_type },
              {
                icon: TrafficIcon,
                label: 'Traffic Record',
                value: (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="View detailed traffic data">
                      <IconButton
                        component={RouterLink}
                        to={`/traffic`}
                        size="small"
                        sx={{
                          color: textColor,
                          padding: '2px',
                          '&:hover': {
                            backgroundColor: `${primaryColor}20`,
                          },
                        }}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )
              },
            ].map((item, index) => (
                <Grid item xs={12} key={index}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center">
                      <item.icon sx={{ mr: 1, color: textColor }} />
                      <Typography variant="body2" sx={{ color: textColor }}>
                        {item.label}:
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      {item.link ? (
                        <Link
                          component={RouterLink}
                          to={item.link.url}
                          sx={{
                            color: textColor,
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          <Typography variant="body2" sx={{ color: textColor, fontWeight: 'bold' }}>
                            {item.link.text}
                          </Typography>
                        </Link>
                      ) : (
                        <Typography variant="body2" sx={{ color: textColor, fontWeight: 'bold' }}>
                          {item.value}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
        </GridItem>
        {/* Status */}
        <GridItem gridArea="1 / 3 / 2 / 4">
          <Typography variant="h6" gutterBottom sx={{ color: textColor }}>
            Device Status
          </Typography>
          
          <Grid container spacing={2}>
            {[
              { 
                icon: CellWifiIcon, 
                label: 'Connection',
                value: (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title={device?.status?.live ? 'Connected' : 'Low Signal'}>
                      <FiberManualRecordIcon 
                        sx={{ 
                          color: device?.status?.live ? 'green' : '#FF9800',
                          fontSize: '16px'
                        }} 
                      />
                    </Tooltip>
                    <Typography variant="body2" sx={{ color: textColor, fontWeight: 'bold' }}>
                      {`${device.status?.dbm? device.status.dbm : 'N/A'} dBm (${device?.status?.network_type? device.status.network_type : 'Unknown'})`}
                    </Typography>
                  </Box>
                )
              },
              {
                icon: BatteryFullIcon,
                label: 'Battery',
                value: (() => {
                  const batteryPercentage = device?.status?.battery_rsoc != null ? `${device.status.battery_rsoc}%` : 'N/A';
                  let runningStatus = 'Unknown';
                  if (device?.status?.running_status != null) {
                    switch (device.status.running_status) {
                      case 1:
                        runningStatus = 'Normal';
                        break;
                      case 2:
                        runningStatus = 'Power Saving';
                        break;
                      default:
                        runningStatus = 'Unknown';
                    }
                  }
                  return `${batteryPercentage} (${runningStatus})`;
                })()
              },
              { 
                icon: ThermostatIcon, 
                label: 'Cabin / Battery Temp', 
                value: cabinTemp !== null && cabinTemp != false ? `${cabinTemp}°C / ${batteryTemp}°C` : 'N/A',
                loading: cabinTempLoading,
                error: cabinTempError
              },
              { 
                icon: GpsFixedIcon, 
                label: 'Last Known GPS', 
                value: `${device?.status?.lat_number ? device.status.lat_number: ''} ${device?.status?.lat_letter ? device.status.lat_letter : 'N/A'}, ${device?.status?.lon_number ? device.status.lon_number : ''} ${device?.status?.lon_letter ? device.status.lon_letter : 'N/A'}` 
              },
              { 
                icon: ScreenRotationIcon, 
                label: 'Screen Angles', 
                value: `${device?.status?.tilt_x ? device.status.tilt_x : 0}°, ${device?.status?.tilt_y ? device.status.tilt_y : 0}°, ${device?.status?.tilt_z ? device.status.tilt_z : 0}°` 
              },
              { 
                icon: StorageIcon, 
                label: 'Storage Remaining', 
                loading: storageRemainingLoading,
                value: (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ color: textColor, fontWeight: 'bold' }}>
                      {storageRemaining ? `${storageRemaining} MB` : 'N/A'}
                    </Typography>
                    <Tooltip title="View storage details">
                      <IconButton
                        component={RouterLink}
                        to={`/file-management/${device.id}`}
                        size="small"
                        sx={{
                          color: textColor,
                          padding: '2px',
                          '&:hover': {
                            backgroundColor: `${primaryColor}20`,
                          },
                        }}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )
              },
              { 
                icon: AccessTimeIcon, 
                label: 'Device Time', 
                loading: deviceTimeLoading,
                value: (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ color: textColor, fontWeight: 'bold' }}>
                     {deviceTime ? `${formatDeviceTime(deviceTime)}` : 'N/A'}
                    </Typography>
                    <Tooltip title="View time details">
                      <IconButton onClick={fetchDeviceTimeRefresh} size="small">
                        <RefreshIcon fontSize="small" sx={{ color: textColor }} />
                      </IconButton>
                      <IconButton
                        onClick={() => handleOpenTimeDialog(device.status.device_time)}
                        size="small"
                        sx={{
                          color: textColor,
                          padding: '2px',
                          '&:hover': {
                            backgroundColor: `${primaryColor}20`,
                          },
                        }}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )
              },
            ].map((item, index) => (
              <Grid item xs={12} key={index}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center">
                    <item.icon sx={{ mr: 1, color: textColor }} />
                    <Typography variant="body2" sx={{ color: textColor }}>
                      {item.label}:
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    {item.loading ? (
                      <CircularProgress size={20} />
                    ) : item.error ? (
                      <Tooltip title={item.error}>
                        <Typography variant="body2" sx={{ color: 'error.main' }}>
                          Failed to retrieve
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" sx={{ color: textColor, fontWeight: 'bold' }}>
                        {item.value}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </GridItem>
        <GridItem gridArea="2 / 1 / 3 / 3">
            <Typography variant="h6" gutterBottom>
                Display
                <Typography 
                    component="span" 
                    style={{ 
                    ...indicatorStyle,
                    color: displayLive ? 'green' : 'cyan',
                    }}
                >
                    {displayLive ? 'Live' : 'Record'}
                </Typography>
            </Typography>
            <Grid container spacing={2}>
            <Grid item xs={4}>
                <Grid container direction="column" spacing={2}>
                  <Grid item xs={4}>

                    <FormControlLabel
                    control={
                        <Switch
                        checked={playing?.flash === 1}
                        onChange={(event) => handlePlayingChange('flash', event.target.checked ? 1 : 0)}
                        disabled={!playing || isLocked}
                        />
                    }
                    label={<Typography sx={{ color: textColor }}>Flash</Typography>}
                  />
                </Grid>
                <Grid item>
                  <EditableTextFieldLock
                    label="Delay (sec)"
                    initialValue={playing?.displayOn ?? 0}
                    onChange={(value) => {
                      if (!isLocked) {
                        handlePlayingChange('displayOn', value);
                      }
                    }}
                    disabled={!playing || isLocked || true}
                    type="number"
                    min={1}
                    max={60}
                    step={1}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Dialog 
              open={openImageDialog} 
              onClose={handleCloseImageDialog}
              PaperProps={{
                style: {
                  backgroundColor: primaryColor,
                  color: textColor,
                },
              }}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle sx={{ color: textColor }}>
                Select {imageDialogType === 'regular' ? 'Regular' : 'Speeding'} Image
              </DialogTitle>
              <DialogContent>
                {isLoadingImages ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {Object.entries(userMediaImages).map(([fileName, blobUrl]) => (
                      <Grid item xs={12} sm={6} key={fileName}>
                        <Paper 
                          onClick={() => handleImageSelect(fileName)}
                          sx={{ 
                            cursor: 'pointer',
                            backgroundColor: selectedImage === fileName ? 'green' : secondaryColor,
                            '&:hover': {
                              backgroundColor: selectedImage === fileName ? 'darkgreen' : 'rgba(255, 255, 255, 0.1)',
                            },
                            p: 2,
                          }}
                        >
                          <Grid container spacing={6} alignItems="center">
                            <Grid item xs={6}>
                              <Box
                                sx={{
                                  width: '100%',
                                  paddingTop: '75%',
                                  position: 'relative',
                                  backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                                }}
                              >
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                  }}
                                >
                                  <img
                                    src={blobUrl}
                                    alt={fileName}
                                    style={{ 
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'contain',
                                      aspectRatio: '64 / 48',
                                    }}
                                  />
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="subtitle1" sx={{ color: textColor, wordBreak: 'break-word' }}>
                                {fileName}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseImageDialog} sx={{ color: textColor }}>Cancel</Button>
              </DialogActions>
            </Dialog>
            <TimeDialog 
              open={openTimeDialog} 
              onClose={handleCloseTimeDialog}
              primaryColor={primaryColor}
              textColor={textColor}
            />
            <Grid item xs={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                <Typography variant="h6">Regular Preview</Typography>
                {playing === null ? (
                    <CircularProgress size={40} />
                ) : playing?.regularContentName ? (
                  <ContentPreview 
                    contentMode={playing.regularContent}
                    regularContent={mediaImages[playing.regularContentName]}
                    speedingContent={mediaImages[playing.speedingContentName]}
                    imageDisplayDuration={playing.switchFreqOn}
                    speedDisplayDuration={playing.switchFreqOff}
                    speedLimit={playing.speedLimit}
                    isRegular={true}
                    flash={playing.flash === 1}
                    flashOnDuration={playing.flashFreqOn}
                    flashOffDuration={playing.flashFreqOff}
                  />
                ) : (
                    <SyncProblemIcon color="error" style={{ fontSize: 40 }} />
                )}
                </Box>
            </Grid>
            <Grid item xs={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                <Typography variant="h6">Speeding Preview</Typography>
                {playing === null ? (
                    <CircularProgress size={40} />
                ) : playing?.speedingContentName ? (
                  <ContentPreview 
                    contentMode={playing.speedingContent}
                    regularContent={mediaImages[playing.regularContentName]}
                    speedingContent={mediaImages[playing.speedingContentName]}
                    imageDisplayDuration={playing.switchFreqOn}
                    speedDisplayDuration={playing.switchFreqOff}
                    speedLimit={playing.speedLimit}
                    isRegular={false}
                    flash={playing.flash === 1}
                    flashOnDuration={playing.flashFreqOn}
                    flashOffDuration={playing.flashFreqOff}
                  />
                ) : (
                    <SyncProblemIcon color="error" style={{ fontSize: 40 }} />
                )}
                </Box>
            </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={4}>
              <EditableTextFieldLock
                label="Min Speed (km/h)"
                initialValue={playing?.minSpeed ?? 0}
                onChange={(value) => {
                  if (!isLocked) {
                    handlePlayingChange('minSpeed', value);
                  }
                }}
                disabled={!playing || isLocked}
                type="number"
                min={0}
                max={255}
                step={10}
              />
            </Grid>
            <Grid item xs={4}>
                <TextField
                    select
                    size="small"
                    label="Regular Content"
                    value={playing?.regularContent || ''}
                    onChange={(event) => handlePlayingChange('regularContent', event.target.value)}
                    disabled={!playing}
                    sx={textFieldSx}
                    >
                    <MenuItem value={1}>Regular: Image</MenuItem>
                    <MenuItem value={2}>Regular: Speed</MenuItem>
                    <MenuItem value={3}>Regular: Both</MenuItem>
                </TextField>
            </Grid>
            <Grid item xs={4}>
                <TextField
                    select
                    size="small"
                    label="Speeding Content"
                    value={playing?.speedingContent || ''}
                    onChange={(event) => handlePlayingChange('speedingContent', event.target.value)}
                    disabled={!playing}
                    sx={textFieldSx}
                    >
                    <MenuItem value={1}>Speeding: Image</MenuItem>
                    <MenuItem value={2}>Speeding: Speed</MenuItem>
                    <MenuItem value={3}>Speeding: Both</MenuItem>
                </TextField>
            </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={4}>
              <EditableTextFieldLock
                label="Speed Limit (km/h)"
                initialValue={playing?.speedLimit ?? 0}
                onChange={(value) => {
                  if (!isLocked) {
                    handlePlayingChange('speedLimit', value);
                  }
                }}
                disabled={!playing || isLocked}
                type="number"
                min={0}
                max={255}
                step={10}
              />
            </Grid>
            <Grid item xs={4}>
              <Grid item>
                <Button 
                  fullWidth
                  variant="contained" 
                  onClick={() => handleOpenImageDialog('regular')}
                  sx={{
                    color: textColor,
                    backgroundColor:
                      primaryColor,
                    '&:hover': {
                      backgroundColor: 
                        secondaryColor,
                    },
                    padding: '10px 20px',
                    borderRadius: '5px',
                  }}
                >
                  Update Regular Image
                </Button>
              </Grid>
            </Grid>
            <Grid item xs={4}>
            <Grid item>
                    <Button 
                      fullWidth
                      variant="contained" 
                      onClick={() => handleOpenImageDialog('speeding')}
                      sx={{
                        color: textColor,
                        backgroundColor:
                          primaryColor,
                        '&:hover': {
                          backgroundColor: 
                            secondaryColor,
                        },
                        padding: '10px 20px',
                        borderRadius: '5px',
                      }}
                    >
                      Update Speeding Image
                    </Button>
                    </Grid>
            </Grid>
            </Grid>
            {hasDisplayChanges && (
                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleDisplayUpdate}
                    style={{ marginTop: '20px' }}
                >
                    Update Display
                </Button>
            )}
        </GridItem>
        <GridItem gridArea="2 / 3 / 3 / 4">
          <Typography variant="h6">
            Brightness
            <Typography 
              component="span" 
              style={{ 
                ...indicatorStyle,
                color: brightnessLive ? 'green' : 'cyan',
              }}
            >
              {brightnessLive ? 'Live' : 'Record'}
            </Typography>
          </Typography>
          <BrightnessControl 
            brightness={brightness}
            onChange={(newBrightness) => {
              setBrightness(newBrightness);
              setHasBrightnessChanges(true);
            }}
            isLocked={isLocked}
          />
          {!isLocked && hasBrightnessChanges && (
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleBrightnessUpdate}
              style={{ marginTop: '20px' }}
            >
              Update Brightness
            </Button>
          )}
        </GridItem>

        <GridItem gridArea="3 / 1 / 4 / 2">
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 0 }}>Upcoming Schedules</Typography>
            <TableContainer component={Box} sx={{ flexGrow: 1, overflowY: 'auto' }}>
              <Table stickyHeader aria-label="device schedules table">
                  <TableHead>
                      <TableRow>
                          <TableCell sx={{ backgroundColor: `${primaryColor} !important`, color: `${textColor} !important` }}>Date</TableCell>
                          <TableCell sx={{ backgroundColor: `${primaryColor} !important`, color: `${textColor} !important` }}>Preset</TableCell>
                      </TableRow>
                  </TableHead>
                <TableBody>
                  {deviceSchedules.length > 0 ? (
                    deviceSchedules.map((schedule) => (
                      <TableRow key={schedule.id} sx={{ '&:nth-of-type(even)': { backgroundColor: primaryColor } }}>
                        <TableCell>
                        <Link 
                          component="button"
                          sx={{ color: textColor, textDecoration: 'none' }}
                        >
                          {formatScheduleTime(schedule)}
                        </Link>
                      </TableCell>
                        <TableCell>
                          <Link 
                            component={RouterLink} 
                            to={`/presets/${schedule.preset_id}`}
                            color={textColor}
                          >
                            {schedule.preset_name}
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} align="center">No schedules available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
        </Box>
      </GridItem>
        <GridItem gridArea="3 / 2 / 4 / 4">
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Typography variant="h6" sx={{ mb: 0 }}>Recent Update Histories</Typography>
              <TableContainer component={Box} sx={{ flexGrow: 1, overflow: 'auto' }}>
                <Table stickyHeader aria-label="device schedules table">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ backgroundColor: `${primaryColor} !important`, color: `${textColor} !important`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Date</TableCell>
                      <TableCell sx={{ backgroundColor: `${primaryColor} !important`, color: `${textColor} !important`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Status</TableCell>
                      <TableCell sx={{ backgroundColor: `${primaryColor} !important`, color: `${textColor} !important`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Min Speed</TableCell>
                      <TableCell sx={{ backgroundColor: `${primaryColor} !important`, color: `${textColor} !important`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Speed Limit</TableCell>
                      <TableCell sx={{ backgroundColor: `${primaryColor} !important`, color: `${textColor} !important`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Regular Mode</TableCell>
                      <TableCell sx={{ backgroundColor: `${primaryColor} !important`, color: `${textColor} !important`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Regular Image</TableCell>
                      <TableCell sx={{ backgroundColor: `${primaryColor} !important`, color: `${textColor} !important`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Speeding Mode</TableCell>
                      <TableCell sx={{ backgroundColor: `${primaryColor} !important`, color: `${textColor} !important`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Speeding Image</TableCell>
                      <TableCell sx={{ backgroundColor: `${primaryColor} !important`, color: `${textColor} !important`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Flash</TableCell>
                      <TableCell sx={{ backgroundColor: `${primaryColor} !important`, color: `${textColor} !important`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Brightness % (24 hrs)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deviceUpdateLogs.map((log) => (
                      <TableRow key={log.id} sx={{ '&:nth-of-type(even)': { backgroundColor: primaryColor } }}>
                        <TableCell sx={{ color: `${textColor} !important`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{new Date(log.created_at).toLocaleString()}</TableCell>
                        <TableCell sx={{ color: `${textColor} !important`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{log.status ? 'Success' : 'Pending'}</TableCell>
                        <TableCell sx={{ color: `${textColor} !important`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{log.min_speed} km/h</TableCell>
                        <TableCell sx={{ color: `${textColor} !important`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{log.speed_limit} km/h</TableCell>
                        <TableCell sx={{ color: `${textColor} !important`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{log.regular_display_mode === 1 ? 'Image' : log.regular_display_mode === 2 ? 'Speed' : 'Both'}</TableCell>
                        <TableCell sx={{ color: `${textColor} !important`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{log.regular_content.split(':')[1]}</TableCell>
                        <TableCell sx={{ color: `${textColor} !important`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{log.speeding_display_mode === 1 ? 'Image' : log.speeding_display_mode === 2 ? 'Speed' : 'Both'}</TableCell>
                        <TableCell sx={{ color: `${textColor} !important`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{log.speeding_content.split(':')[1]}</TableCell>
                        <TableCell sx={{ color: `${textColor} !important`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{log.flash === 1 ? 'On' : 'Off'}</TableCell>
                        <TableCell sx={{ color: `${textColor} !important`, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {
                            log.brightness && log.brightness.length > 0 
                              ? log.brightness.map((b, index) => 
                                  index === log.brightness.length - 1 ? b : `${b} > `
                                ).join('')
                              : 'N/A'
                          }
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
          </Box>
        </GridItem>
         <GridItem gridArea="4 / 1 / 5 / 4">
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Typography variant="h6" sx={{ mb: 0 }}>Service Logs</Typography>
              {device.serviceLogs.length > 0 ? (
                <TableContainer component={Box} sx={{ flexGrow: 1, overflow: 'auto', mt: 2 }}>
                  <Table stickyHeader aria-label="service logs table">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ backgroundColor: `${primaryColor} !important`, color: `${textColor} !important` }}>Date</TableCell>
                        <TableCell sx={{ backgroundColor: `${primaryColor} !important`, color: `${textColor} !important` }}>Service Type</TableCell>
                        <TableCell sx={{ backgroundColor: `${primaryColor} !important`, color: `${textColor} !important` }}>Performed By</TableCell>
                        <TableCell sx={{ backgroundColor: `${primaryColor} !important`, color: `${textColor} !important` }}>Description</TableCell>
                        <TableCell sx={{ backgroundColor: `${primaryColor} !important`, color: `${textColor} !important` }}>Details</TableCell>
                        <TableCell sx={{ backgroundColor: `${primaryColor} !important`, color: `${textColor} !important` }}>Options</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {device.serviceLogs.map((log) => (
                        <TableRow key={log.id}>
                           <TableCell sx={{ color: textColor }}>
                            {(() => {
                              const date = new Date(log.created_at);
                              date.setHours(date.getHours() + 10);
                              return date.toLocaleDateString();
                            })()}
                          </TableCell>
                          <TableCell sx={{ color: textColor }}>{log.serviceType}</TableCell>
                          <TableCell sx={{ color: textColor }}>{log.performedBy}</TableCell>
                          <TableCell sx={{ color: textColor }}>{log.description}</TableCell>
                          <TableCell sx={{ color: textColor }}>{log.details}</TableCell>
                          <TableCell sx={{ color: textColor }}>
                            <Button 
                              variant="outlined" 
                              size="small"
                              onClick={() => handleServiceLogClick(log)}
                              sx={{ 
                                color: textColor, 
                                borderColor: textColor,
                                '&:hover': {
                                  backgroundColor: primaryColor,
                                  borderColor: textColor
                                }
                              }}
                            >
                              More
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography sx={{ mt: 2, color: textColor }}>No service logs found.</Typography>
              )}
          </Box>
        </GridItem>
      </div>
      <Dialog 
        open={serviceLogDialogOpen} 
        onClose={handleServiceLogDialogClose}

        PaperProps={{
          style: {
            backgroundColor: primaryColor,
            color: textColor,
          },
        }}
      >
        <DialogTitle sx={{ color: textColor }}>Service Log Details</DialogTitle>
        <DialogContent>
          {selectedServiceLog && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Date:</Typography>
              <Typography sx={{ mb: 2 }}>
                {(() => {
                  const date = new Date(selectedServiceLog.created_at);
                  date.setHours(date.getHours() + 10);
                  return date.toLocaleString();
                })()}
              </Typography>
              
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Service Type:</Typography>
              <Typography sx={{ mb: 2 }}>{selectedServiceLog.serviceType}</Typography>
              
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Performed By:</Typography>
              <Typography sx={{ mb: 2 }}>{selectedServiceLog.performedBy}</Typography>
              
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Description:</Typography>
              <Typography sx={{ mb: 2 }}>{selectedServiceLog.description}</Typography>
              
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Details:</Typography>
              <Typography sx={{ mb: 2 }}>{selectedServiceLog.details}</Typography>

              {serviceImages.length > 0 && (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Images:</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                    {serviceImages.map((image, index) => (
                       <Box 
                        key={index} 
                        sx={{ 
                          width: enlargedImageIndex === index ? '100%' : 100, 
                          height: enlargedImageIndex === index ? '100%' : 100, 
                          border: `1px solid ${textColor}`,
                          borderRadius: 1,
                          overflow: 'hidden',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          if (enlargedImageIndex === index) {
                            setEnlargedImageIndex(-1);
                          } else {
                            setEnlargedImageIndex(index);
                          }
                        }} 
                      >
                        <img 
                          src={image.data} 
                          alt={`Service log image ${index + 1}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleServiceLogDialogClose} sx={{ color: textColor }}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
            sx: {
                backgroundColor: 'transparent',
                boxShadow: 'none',
                overflow: 'visible',
                maxWidth: 'none',
                maxHeight: 'none',
                margin: 0
            }
        }}
        BackdropProps={{
            sx: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                backdropFilter: 'blur(3px)'
            }
        }}
      >
        <Box 
            sx={{
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                width: '100vw',
                overflow: 'hidden'
            }}
        >
            {enlargedImage && (
                <Box
                    sx={{
                        position: 'relative',
                        transformOrigin: 'center center',
                        transition: 'transform 0.1s ease',
                        cursor: 'grab',
                        '&:active': {
                            cursor: 'grabbing'
                        }
                    }}
                    onWheel={(e) => {
                        e.preventDefault();
                        const scaleDelta = e.deltaY > 0 ? 0.9 : 1.1;
                        setImageScale(prev => Math.max(0.1, Math.min(5, prev * scaleDelta)));
                    }}
                    onMouseDown={(e) => {
                        if (e.button === 0) {
                            setIsDragging(true);
                            setDragStart({ x: e.clientX, y: e.clientY });
                            setImagePosition({ x: 0, y: 0 });
                        }
                    }}
                    onMouseMove={(e) => {
                        if (isDragging) {
                            const deltaX = e.clientX - dragStart.x;
                            const deltaY = e.clientY - dragStart.y;
                            setImagePosition({ x: deltaX, y: deltaY });
                        }
                    }}
                    onMouseUp={() => setIsDragging(false)}
                    onMouseLeave={() => setIsDragging(false)}
                >
                    <img
                        src={enlargedImage}
                        alt="Enlarged view"
                        style={{ 
                            maxWidth: '90vw',
                            maxHeight: '90vh',
                            objectFit: 'contain',
                            display: 'block',
                            transform: `scale(${imageScale}) translate(${imagePosition.x}px, ${imagePosition.y}px) rotate(${imageRotation}deg)`,
                            cursor: isDragging ? 'grabbing' : 'grab',
                            transition: 'transform 0.1s ease'
                        }}
                        onClick={(e) => {
                            if (!isDragging) {
                                setImageDialogOpen(false);
                            }
                        }}
                    />
                </Box>
            )}
            <Box 
                sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    display: 'flex',
                    gap: 1,
                    zIndex: 1
                }}
            >
                <Button
                    variant="contained"
                    size="small"
                    onClick={() => setImageScale(prev => Math.min(5, prev * 1.2))}
                    sx={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', color: 'white' }}
                >
                    +
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    onClick={() => setImageScale(prev => Math.max(0.1, prev * 0.8))}
                    sx={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', color: 'white' }}
                >
                    -
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                        setImageScale(1);
                        setImagePosition({ x: 0, y: 0 });
                        setImageRotation(0);
                    }}
                    sx={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', color: 'white' }}
                >
                    Reset
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    onClick={() => setImageRotation(prev => (prev + 90) % 360)}
                    sx={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', color: 'white' }}
                >
                    ↻
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    onClick={() => setImageRotation(prev => (prev - 90 + 360) % 360)}
                    sx={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', color: 'white' }}
                >
                    ↺
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    onClick={() => setImageDialogOpen(false)}
                    sx={{ backgroundColor: 'rgba(255, 0, 0, 0.5)', color: 'white' }}
                >
                    X
                </Button>
            </Box>
        </Box>
    </Dialog>
      <Dialog 
      open={openDialog} 
      onClose={handleDialogClose}
      PaperProps={{
        style: {
          backgroundColor: primaryColor,
          color: textColor,
        },
      }}
      >
        <DialogTitle sx={{ color: textColor }}>Enter Control Password</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="password"
            label="Control Password"
            type="password"
            fullWidth
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!dialogError}
            helperText={dialogError}
            sx={{
              '& .MuiInputBase-input': { color: textColor },
              '& .MuiInputLabel-root': { color: textColor },
              '& .MuiOutlinedInput-root': { 
                '& fieldset': { borderColor: textColor },
                '&:hover fieldset': { borderColor: textColor },
                '&.Mui-focused fieldset': { borderColor: textColor },
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} sx={{ color: textColor }}>Cancel</Button>
          <Button onClick={handlePasswordSubmit} sx={{ color: textColor }}>Submit</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};





























/*
<Grid item xs={4}>
  <Tooltip title="Can only be edited while at least one content is set to 'Both'">
    <EditableTextField
      label="Image Display Duration (sec)"
      initialValue={playing?.switchFreqOn ?? 0}
      onChange={(value) => {
          handlePlayingChange('switchFreqOn', value);
      }}
      disabled={!playing || (playing?.regularContent !== 3 && playing?.speedingContent !== 3)}                        
      type="number"
      min={1}
      max={60}
      step={1}
    />
  </Tooltip>
</Grid>
<Grid item xs={4}>
  <Tooltip title="Can only be edited while at least one content is set to 'Both'">
    <EditableTextField
      label="Speed Display Duration (sec)"
      initialValue={playing?.switchFreqOff ?? 0}
      onChange={(value) => {
          handlePlayingChange('switchFreqOff', value);
      }}
      disabled={!playing || (playing?.regularContent !== 3 && playing?.speedingContent !== 3)}
      type="number"
      min={1}
      max={60}
      step={1}
    />
  </Tooltip>
</Grid>
================================

<Grid item xs={4}>
  <EditableTextField
    label="Flash On Duration (sec)"
    initialValue={playing?.flashFreqOn ?? 0}
    onChange={(value) => {
        handlePlayingChange('flashFreqOn', value);
    }}
    disabled={!playing || !playing.flash}
    type="number"
    min={0.1}
    max={60}
    step={0.1}
  />
</Grid>
<Grid item xs={4}>
  <EditableTextField
    label="Flash Off Duration (sec)"
    initialValue={playing?.flashFreqOff ?? 0}
    onChange={(value) => {
        handlePlayingChange('flashFreqOff', value);
    }}
    disabled={!playing || !playing.flash}
    type="number"
    min={0.1}
    max={60}
    step={0.1}
  />
</Grid>

*/
export default Device;
